// src/stores/useAppStore.js
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

const TEAMS_KEY = 'zinboard:teams:v1';
const AUTH_KEY = 'auth';

const isBrowser = typeof window !== 'undefined' && !!window.localStorage;

/* ----------------------------------------
   Load Teams from LocalStorage
----------------------------------------- */
const loadTeamsFromStorage = () => {
  if (!isBrowser) return { teams: [], activeTeamId: null };
  try {
    const raw = localStorage.getItem(TEAMS_KEY);
    if (!raw) return { teams: [], activeTeamId: null };
    const parsed = JSON.parse(raw);
    return {
      teams: Array.isArray(parsed?.teams) ? parsed.teams : [],
      activeTeamId: parsed?.activeTeamId ?? null,
    };
  } catch {
    return { teams: [], activeTeamId: null };
  }
};

const saveTeamsToStorage = (teams, activeTeamId) => {
  if (!isBrowser) return;
  try {
    localStorage.setItem(
      TEAMS_KEY,
      JSON.stringify({ teams, activeTeamId })
    );
  } catch {}
};

/* ----------------------------------------
   Zustand Store
----------------------------------------- */
export const useAppStore = create((set) => {
  const stored = loadTeamsFromStorage();

  return {
    /* ---------------- Auth ---------------- */
    authToken: null,
    currentUser: null,
    organization: null,
    authenticated: false,
    authHydrated: false,

    /** Save new token + user info */
    setAuthSnapshot: ({ token, user, org }) => {
      try {
        if (isBrowser) {
          localStorage.setItem(
            AUTH_KEY,
            JSON.stringify({
              token,
              user: user || null,
              org: org || null
            })
          );
        }
      } catch {}

      set({
        authToken: token,
        currentUser: user || null,
        organization: org || null,
        authenticated: !!token,
        authHydrated: true,
      });
    },

    /** Hydrate auth state on first page load */
    hydrateAuthFromStorage: () => {
      if (!isBrowser) {
        set({ authHydrated: true });
        return;
      }

      try {
        const raw = localStorage.getItem(AUTH_KEY);
        if (!raw) {
          set({
            authToken: null,
            currentUser: null,
            organization: null,
            authenticated: false,
            authHydrated: true,
          });
          return;
        }

        const parsed = JSON.parse(raw);

        set({
          authToken: parsed?.token || null,
          currentUser: parsed?.user || null,
          organization: parsed?.org || null,
          authenticated: !!parsed?.token,
          authHydrated: true,
        });
      } catch {
        set({ authHydrated: true });
      }
    },

    /** Logout fully */
    logout: () => {
      try {
        if (isBrowser) localStorage.clear(AUTH_KEY);
      } catch {}

      set({
        authToken: null,
        currentUser: null,
        organization: null,
        authenticated: false,
        authHydrated: true,
      });
    },

    /* ---------------- Teams ---------------- */
    teams: stored.teams || [],
    activeTeamId: stored.activeTeamId || null,

    setTeams: (teamsPayload) => {
      set((state) => {
        const teams = Array.isArray(teamsPayload)
          ? teamsPayload.map((t) => ({
              id: t.id || uuidv4(),
              name: t.name || 'Untitled',
              memberIds: Array.isArray(t.memberIds) ? t.memberIds : [],
              orgId: t.orgId || null,
              managerId: t.managerId || null,
            }))
          : [];

        saveTeamsToStorage(teams, state.activeTeamId);
        return { teams };
      });
    },

    setActiveTeam: (id) => {
      set((state) => {
        saveTeamsToStorage(state.teams, id);
        return { activeTeamId: id };
      });
    },
  };
});
